import React, { useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import ru from 'date-fns/locale/ru';
import { LangKey } from 'config';
import { useTranslation } from 'react-i18next';
import { DATE_FORMAT, MIN_DATE, MAX_DATE } from './CustomDatePicker.constants';
import { CustomDatePickerProps } from './CustomDatePicker.model';
import {
  CustomDateInput,
  CustomDatePickerHeader,
  CustomDayContent,
} from './components';
import * as S from './CustomDatePicker.styles';
import { boxDate } from './utils/utils';

registerLocale(LangKey.ru, ru);

export const CustomDatePicker = ({
  onChange,
  selected,
  placeholderText,
  disabled,
  requiredText,
  minDate = MIN_DATE,
  maxDate = MAX_DATE,
  ignoreErrors,
  customInputStyles,
  ...restProps
}: CustomDatePickerProps) => {
  const {
    i18n: { language },
  } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const openCalendar = () => {
    setIsOpen(true);
  };

  const closeCalendar = () => {
    setIsOpen(false);
  };

  const getLocalizedPlaceholder = () => {
    switch (language) {
      case LangKey.ru:
        return 'дд/мм/гг';
      case LangKey.en:
      default:
        return 'dd/mm/yy';
    }
  };

  return (
    <S.DatePickerWrapper data-testid='date-picker'>
      <S.DatePickerGlobalStyles />
      <DatePicker
        portalId='date-picker-portal'
        dateFormat={DATE_FORMAT}
        shouldCloseOnSelect={false}
        open={isOpen}
        onInputClick={openCalendar}
        onClickOutside={closeCalendar}
        showMonthDropdown
        showYearDropdown
        locale={language}
        dropdownMode='select'
        nextMonthButtonLabel={false}
        forceShowMonthNavigation={false}
        showDisabledMonthNavigation={false}
        calendarStartDay={1}
        placeholderText={isOpen ? getLocalizedPlaceholder() : placeholderText}
        disabled={disabled}
        onChange={onChange}
        selected={selected}
        customInput={
          <CustomDateInput
            disabled={disabled}
            onClickOutside={closeCalendar}
            changeHandler={onChange}
            requiredText={requiredText}
            ignoreErrors={ignoreErrors}
            isDatePickerOpen={isOpen}
            isValueSelected={!!selected}
            dateInputStart={boxDate(restProps.startDate ?? null) ?? undefined}
            customStyles={customInputStyles}
          />
        }
        showPopperArrow={false}
        fixedHeight
        renderCustomHeader={({ changeYear, changeMonth, date }) => (
          <CustomDatePickerHeader
            changeYear={changeYear}
            changeMonth={changeMonth}
            selected={selected}
            date={date}
          />
        )}
        renderDayContents={(day: number, date: Date) => (
          <CustomDayContent day={day} date={date} />
        )}
        preventOpenOnFocus
        minDate={minDate}
        maxDate={maxDate}
        popperModifiers={[
          {
            name: 'flip',
            enabled: false,
          },
        ]}
        {...restProps}
      />
    </S.DatePickerWrapper>
  );
};
